import type { WarningFixEntry } from "verdi-common/schemas/warning/subSchemas/warningFixEntry";
import type { WarningProblemEntry } from "verdi-common/schemas/warning/subSchemas/warningProblemEntry";

export type WarningType =
    | "offline"
    | "poor connection"
    | "low accuracy"
    | "high sensor reading"
    | "low sensor reading"
    | "zero sensor reading"
    | "no flow"
    | "RTC sync error"
    | "misconfigured device";

// The schema subdocument classes are the single source of truth for entry shapes;
// serialized API entries carry their `schemaKey` virtual, so payloads satisfy them.
export type DeviceWarningProblem = WarningProblemEntry;
export type DeviceWarningFix = WarningFixEntry;

export class DeviceWarning {
    constructor(json) {
        this.warningTitle = json.warningTitle;
        this.warningDetails = json.warningDetails;
        this.warningLevel = json.warningLevel;
        this.warningType = json.warningType;
        this.warningTypePriority = json.warningTypePriority;
        this.warningVariant = json.warningVariant;
        this.warningParams = json.warningParams;
        this.warningProblems = json.warningProblems;
        this.warningFixes = json.warningFixes;
    }
    public warningType!: WarningType;

    public warningTitle!: string;

    public warningDetails?: string;

    /**
     * Identifies the specific sub-form of this warning. Defaults to BASE on the server
     * side; older documents written before this field existed will hydrate as BASE via
     * the schema default.
     */
    public warningVariant?: string;

    /**
     * Runtime values for interpolating the title/description templates (e.g. sensor
     * readings, thresholds, names). Read by the renderer via i18next placeholder
     * substitution.
     */
    public warningParams?: Record<string, string | number>;

    /**
     * Optional list of structured "potential causes" rendered as a bulleted list under
     * the description. Each entry's `problemKey` is a catalog identifier; the
     * dashboard renders the matching translation under `<warningType>.problems.<key>`.
     */
    public warningProblems?: DeviceWarningProblem[];

    /**
     * Optional list of structured "suggested fixes" rendered as a bulleted list of
     * remediation steps. Each entry's `fixKey` is a catalog identifier; the dashboard
     * renders the matching translation under `<warningType>.fixes.<key>`.
     */
    public warningFixes?: DeviceWarningFix[];

    /**
     * Warning level 0 is info
     * level 1 is warning
     * level 2 is error
     * higher than 2 is same as 2.
     * @type {number}
     */
    public warningLevel!: number;

    /**
     * Priority of warnings of this type. Warnings that are "causes" have a high priority, while warnings that
     * are "effects" have low priority. An offline device will have high packet loss, low accuracy, etc, but
     * the important part is that it's offline
     * @type {string}
     */
    public warningTypePriority!: number;
}
